import apiEndpoint from '../../utils/apiEndpoint'
import AbstractApiClient from './AbstractApiClient'
import { buildApiUrl } from '../../utils/apiUtils'
import { Temporal } from '@js-temporal/polyfill'

const API_URL = import.meta.env.VITE_API_URL

export interface GetTransactionsArgs {
  filters: any
  limit?: number
  offset?: number
  sorts: any[]
  signal?: AbortSignal
}

export interface GetTransientDriversArgs {
  locationIds: number[]
  start: Temporal.PlainDate
  end: Temporal.PlainDate
  signal?: AbortSignal
}

export interface GetKpisArgs {
  locationIds: number[]
  channels: string[]
  start: Temporal.PlainDate
  end: Temporal.PlainDate
  signal?: AbortSignal
}

export interface GetKpisResponse {
  current: KpiPeriod
  previous: KpiPeriod
}

export interface KpiPeriod {
  total_gross_revenue: number
  total_net_revenue: number
  average_rate: number
  total_transactions: number
  cancellation_rate: number
  average_daily_transactions: number
  average_duration: number
  median_duration: number
  average_booking_window: number
  median_booking_window: number
  revpas: number
  average_occupancy: number
}

export interface GetDailyRevenueArgs {
  locationIds: number[]
  channels: string[]
  start: Temporal.PlainDate
  end: Temporal.PlainDate
  signal?: AbortSignal
}

export interface GetDailyRevenueResponse {
  metrics: {
    [key: string]: {
      parcs_revenue_cents: number
      parcs_inventory: number
      reservation_revenue_cents: number
      reservation_inventory: number
      total_revenue_cents: number
      total_inventory: number
      inventory_percentage: number
      revpas_cents: number
    }
  }
}

export interface GetOnLotByWeekdayArgs {
  locationIds: number[]
  start: Temporal.PlainDate
  end: Temporal.PlainDate
  signal?: AbortSignal
}

export interface GetBookingDurationArgs {
  locationIds: number[]
  start: Temporal.PlainDate
  end: Temporal.PlainDate
  signal?: AbortSignal
}


export class ReportingApiClass extends AbstractApiClient {

  getDateSummaries({
    locationIds,
    start,
    end,
    signal
  }: GetDateSummariesRequest): Promise<ReportingDateSummaries> {

    const url = `${API_URL}/reporting/summaries_by_date?location_ids=${locationIds.values().toArray().join(',')}&start=${start}&end=${end}`

    return apiEndpoint({
      url: url,
      method: 'get',
    }, signal)

  }

  getKPIs({
    locationIds,
    channels = [],
    start,
    end,
    signal = undefined,
  }: GetKpisArgs): Promise<GetKpisResponse> {

    const filters: any = {
      location_ids: locationIds,
      start: start.toString(),
      end: end.toString(),
    }

    if (channels.length) {
      filters.channels = channels.join(',')
    }

    const url = buildApiUrl(`${API_URL}/reporting/kpis`, {filters})

    return apiEndpoint({
      url: url,
      method: 'get',
    }, signal)

  }

  getTransactions({
    filters,
    limit,
    offset,
    sorts,
    signal = undefined,
  }: GetTransactionsArgs) {

    const url = buildApiUrl(`${API_URL}/reporting/transactions`, {
      filters,
      limit,
      offset,
      sorts,
    })
    
    return apiEndpoint({
      url: url,
      method: 'get',
    }, signal)

  }

  getActivity(
    locationIds: number[],
    start: Temporal.PlainDate,
    end: Temporal.PlainDate,
    signal?: AbortSignal,
  ): Promise<ReportingGetActivityResponse> {

    const filters = {
      location_ids: locationIds,
      start: start.toString(),
      end: end.toString(),
    }

    const url = buildApiUrl(`${API_URL}/reporting/activity`, {filters})

    return apiEndpoint({url: url, method: 'get'}, signal)
      .then(response => {
        // hydrate times
        Object.values(response).forEach((entry: any) => {
          if (entry.max_on_lot.window_start) {
            entry.max_on_lot.window_start = Temporal.PlainTime.from(entry.max_on_lot.window_start)
          }
          Object.values(entry.max_on_lot.by_channel).forEach((channelEntry: any) => {
            channelEntry.window_start = Temporal.PlainTime.from(channelEntry.window_start)
          })
        })
        return response
      })

  }

  getOnLotByWeekday({
    locationIds,
    start,
    end,
    signal = undefined,
  }: GetOnLotByWeekdayArgs): Promise<GetOnLotByWeekdayResponse> {

    const filters = {
      location_ids: locationIds,
      start: start,
      end: end,
    }

    const url = buildApiUrl(`${API_URL}/reporting/on_lot_by_weekday`, {filters})

    return apiEndpoint({
      url: url,
      method: 'get',
    }, signal)

  }

  getBookingDuration({
    locationIds,
    start,
    end,
    signal = undefined,
  }: GetBookingDurationArgs): Promise<GetBookingDurationResponse> {

    const url = `${API_URL}/reporting/booking_duration?location_ids=${locationIds.join(',')}&start=${start.toString()}&end=${end.toString()}`

    return apiEndpoint({
      url: url,
      method: 'get',
    }, signal)

  }

  getTransientDrivers({
    locationIds,
    start,
    end,
    signal = undefined,
  }: GetTransientDriversArgs): Promise<GetTransientDriversResponse> {

    const url = `${API_URL}/reporting/get_transient_drivers?location_ids=${locationIds.join(',')}&start=${start.toString()}&end=${end.toString()}`

    return apiEndpoint({
      url: url,
      method: 'get',
    }, signal)

  }

  getDailyRevenue({
    locationIds,
    channels = [],
    start,
    end,
    signal = undefined,
  }: GetDailyRevenueArgs): Promise<GetDailyRevenueResponse> {

    const filters: any = {location_ids: locationIds, start: start.toString(), end: end.toString()}

    if (channels.length) {
      filters.channels = channels.join(',')
    }

    const url = buildApiUrl(`${API_URL}/reporting/daily_revenue`, {filters})

    return apiEndpoint({
      url: url,
      method: 'get',
    }, signal)

  }

  getVelocity(
    locationIds: Set<number>,
    start: Temporal.PlainDate,
    end: Temporal.PlainDate,
    locationProductIds?: Set<number>,
    vertical?: string,
    signal?: AbortSignal,
  ): Promise<GetVelocityResponse> {

    let url = `${API_URL}/reporting/velocity?location_ids=${locationIds.values().toArray().join(',')}&start=${start.toString()}&end=${end.toString()}`

    if (locationProductIds) {
      url += `&location_product_ids=${locationProductIds.values().toArray().join(',')}`
    }

    if (vertical) {
      url += `&vertical=${encodeURIComponent(vertical)}`
    }

    return apiEndpoint({
      url: url,
      method: 'get',
    }, signal)
    .then(response => {
      // convert types which can't be represented in JSON (we might want to find or build a reusable lib for this)
      const convertedResponse: GetVelocityResponse = new Map()
      Object.entries(response).forEach(([isoDate, rawDateEntry]: [string, any]) => {
        const dateEntry: GetVelocityResponseDateEntry = new Map()
        Object.entries(rawDateEntry).forEach(([locationIdStr, rawLocationEntry]: [string, any]) => {
          const locationId = parseInt(locationIdStr, 10)
          const locationEntry: GetVelocityResponseLocationEntry = new Map()
          Object.entries(rawLocationEntry).forEach(([lpIdStr, rawLpEntry]: [string, any]) => {
            const lpId = parseInt(lpIdStr, 10)
            const lpEntry: GetVelocityResponseLpEntry = new Map()
            Object.entries(rawLpEntry).forEach(([hoursStr, velocity]: [string, any]) => {
              const hours = parseInt(hoursStr, 10)
              lpEntry.set(hours, velocity)
            })
            locationEntry.set(lpId, lpEntry)
          })
          dateEntry.set(locationId, locationEntry)
        })
        convertedResponse.set(isoDate, dateEntry)
      })
      return convertedResponse
    })

  }

}

const ReportingApi = new ReportingApiClass()
export { ReportingApi }

export type GetDateSummariesRequest = {
  locationIds: Set<number>
  start: Temporal.PlainDate
  end: Temporal.PlainDate
  signal?: AbortSignal
}

export type ReportingDateSummaries = {[key: string]: ReportingDateSummary}

export interface ReportingDateSummary {
  as_transaction_date: {
    gross_revenue: ReportingDateSummaryDollarAmountBreakdown
    net_revenue: ReportingDateSummaryDollarAmountBreakdown
    transactions: ReportingDateSummaryCountBreakdown
    duration?: ReportingDateSummaryCountBreakdown
  }
  as_start_date: {
    gross_revenue: ReportingDateSummaryDollarAmountBreakdown
    net_revenue: ReportingDateSummaryDollarAmountBreakdown
    transactions: ReportingDateSummaryCountBreakdown
    duration?: ReportingDateSummaryCountBreakdown
  }
}

export interface ReportingDateSummaryDollarAmountBreakdown {
  total: number
  by_vertical: {[key: string]: number}
  by_channel: {[key: string]: number}
}

export interface ReportingDateSummaryCountBreakdown {
  total: number
  by_vertical: {[key: string]: number}
  by_channel: {[key: string]: number}
}


export type ReportingGetActivityResponse = {
  [key: string]: // ISO 8601 date
    ReportingGetActivityResponseEntry
}

export interface ReportingGetActivityResponseEntry {
  arrivals: {
    total: number
    by_channel: {[key: string]: number}
  }
  departures: {
    total: number
    by_channel: {[key: string]: number}
  }
  on_lot: { // deprecated?
    total: number
    by_channel: {[key: string]: number}
  }
  max_on_lot: {
    total: number
    window_start: null | Temporal.PlainTime
    by_location: {[key: number]: number}
    by_channel: {
      [key: string]: {
        count: number
        window_start: Temporal.PlainTime
      }
    }
  }
}

export type GetVelocityResponse = Map<string, GetVelocityResponseDateEntry>
export type GetVelocityResponseDateEntry = Map<number, GetVelocityResponseLocationEntry>
export type GetVelocityResponseLocationEntry = Map<number, GetVelocityResponseLpEntry>
export type GetVelocityResponseLpEntry = Map<number, number>

export interface GetBookingDurationResponse {
  by_weekday: {[key: string]: BookingDurationWeekday}
  segments: {[key: string]: BookingDurationSegment}
}

export interface BookingDurationWeekday {
  by_channel: {
    [key: string]: {
      [key: string]: number
    }
  }
}

export interface BookingDurationSegment {
  from: number
  to: number | null
}

export interface GetTransientDriversResponse {
  by_weekday: {
    [key: string]: {
      one_time: GetTransientDriversResponseItem
      repeat: GetTransientDriversResponseItem
      total: number
    }
  }
  totals: {
    one_time: GetTransientDriversResponseItem
    repeat: GetTransientDriversResponseItem
  }
}

export interface GetTransientDriversResponseItem {
  total: number
  by_channel: {[key: string]: number}
}

export interface GetOnLotByWeekdayResponse {
  [key: string]: {
    num_dates: number
    by_window: {
      [key: string]: {
        total: number
        by_channel: {[key: string]: number}
      }
    }
  }
}